import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  increment,
  arrayUnion,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { ProjectIdGenerator } from '../utils/projectIdGenerator';

class ChatService {
  static projectChatDoc(projectId) {
    if (!projectId) return null;
    const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId);
    if (!normalizedProjectId) return null;
    return doc(db, 'projectChats', normalizedProjectId);
  }

  static projectMessagesCollection(projectId) {
    const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      throw new Error('projectId is required for chat operations');
    }
    return collection(db, 'projectChats', normalizedProjectId, 'messages');
  }

  static projectTypingDoc(projectId) {
    const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      throw new Error('projectId is required for chat operations');
    }
    return doc(db, 'projectChats', normalizedProjectId, 'metadata', 'typing');
  }

  static async ensureProjectChat({
    projectId,
    brandUserId,
    creatorId,
    briefId = null,
    campaignId = null,
    brandName = '',
    creatorName = '',
    briefName = '',
    campaignName = ''
  }) {
    const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      throw new Error('projectId is required for chat creation');
    }

    if (!brandUserId || !creatorId) {
      throw new Error('brandUserId and creatorId are required to ensure chat');
    }

    const normalizedBrandId = String(brandUserId).trim();
    const normalizedCreatorId = String(creatorId).trim();
    const normalizedBriefId = briefId ? String(briefId).trim() : null;
    const normalizedCampaignId = campaignId ? String(campaignId).trim() : null;

    const chatRef = this.projectChatDoc(normalizedProjectId);
    if (!chatRef) {
      throw new Error('projectId is required for chat creation');
    }
    let chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      const baseChatData = {
        chatId: normalizedProjectId,
        projectId: normalizedProjectId,
        brandUserId: normalizedBrandId,
        creatorId: normalizedCreatorId,
        briefId: normalizedBriefId,
        campaignId: normalizedCampaignId,
        brandName,
        creatorName,
        briefName,
        campaignName,
        participants: [normalizedBrandId, normalizedCreatorId],
        status: 'active',
        createdAt: serverTimestamp(),
        closedAt: null,
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        unreadCount: {
          brand: 0,
          creator: 0
        }
      };

      await setDoc(chatRef, baseChatData, { merge: true });
    } else {
      const participants = Array.from(new Set([normalizedBrandId, normalizedCreatorId, ...(chatDoc.data().participants || [])])).filter(Boolean);
      await updateDoc(chatRef, {
        brandUserId: normalizedBrandId,
        creatorId: normalizedCreatorId,
        briefId: normalizedBriefId || chatDoc.data().briefId || null,
        campaignId: normalizedCampaignId || chatDoc.data().campaignId || null,
        brandName: brandName || chatDoc.data().brandName || '',
        creatorName: creatorName || chatDoc.data().creatorName || '',
        briefName: briefName || chatDoc.data().briefName || '',
        campaignName: campaignName || chatDoc.data().campaignName || '',
        participants
      });
    }

    await setDoc(this.projectTypingDoc(normalizedProjectId), {
      brandTyping: false,
      creatorTyping: false,
      brandLastSeen: serverTimestamp(),
      creatorLastSeen: serverTimestamp(),
      participants: Array.from(new Set([normalizedBrandId, normalizedCreatorId]))
    }, { merge: true });

    return normalizedProjectId;
  }

  static async createChat(params) {
    return this.ensureProjectChat(params);
  }

  static async getChat(projectId) {
    if (!projectId) return null;
    const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId);
    if (!normalizedProjectId) return null;
    const chatDoc = await getDoc(this.projectChatDoc(normalizedProjectId));
    if (chatDoc.exists()) {
      return { id: chatDoc.id, chatId: chatDoc.id, ...chatDoc.data() };
    }
    return null;
  }

  static async getChatForProject(projectId) {
    return this.getChat(projectId);
  }

  static async throwIfChatClosed(projectId) {
    const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId);
    const chatRef = this.projectChatDoc(normalizedProjectId);
    if (!chatRef) {
      throw new Error('projectId is required for chat operations');
    }
    const chatDoc = await getDoc(chatRef);
    if (chatDoc.exists() && chatDoc.data().status === 'closed') {
      throw new Error('Chat is closed for this project');
    }
  }

  static async sendMessage({
    projectId,
    senderId,
    senderType, // 'brand' or 'creator'
    senderName,
    content
  }) {
    const trimmedContent = (content || '').trim();
    if (!projectId || !senderId || !senderType || !trimmedContent) {
      throw new Error('Missing required parameters for message sending');
    }

    const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId);
    if (!normalizedProjectId) {
      throw new Error('projectId is required to send messages');
    }
    await this.throwIfChatClosed(normalizedProjectId);

    const chatRef = this.projectChatDoc(normalizedProjectId);
    if (!chatRef) {
      throw new Error('projectId is required for chat operations');
    }
    const chatDoc = await getDoc(chatRef);
    if (!chatDoc.exists()) {
      throw new Error('Chat not found for project');
    }

    const chatData = chatDoc.data();
    const isAuthorizedSender = senderType === 'brand'
      ? chatData.brandUserId === senderId
      : chatData.creatorId === senderId;

    if (!isAuthorizedSender) {
      throw new Error('Unauthorized: You are not a participant in this chat');
    }

    const recipientType = senderType === 'brand' ? 'creator' : 'brand';
    const recipientId = recipientType === 'creator' ? chatData.creatorId : chatData.brandUserId;

    const messagesRef = this.projectMessagesCollection(normalizedProjectId);
    const messageDoc = await addDoc(messagesRef, {
      senderId,
      senderType,
      senderName,
      recipientId,
      content: trimmedContent,
      timestamp: serverTimestamp(),
      readBy: [senderId],
      type: 'text'
    });

    const unreadUpdates = {
      lastMessage: trimmedContent.substring(0, 100),
      lastMessageAt: serverTimestamp(),
      [`unreadCount.${recipientType}`]: increment(1)
    };

    await updateDoc(chatRef, unreadUpdates);

    await this.createMessageNotification({
      projectId: normalizedProjectId,
      recipientId,
      senderId,
      senderName,
      content: trimmedContent,
      senderType
    });

    await this.setTypingIndicator(normalizedProjectId, senderType, false);

    return messageDoc.id;
  }

  static async createMessageNotification({
    projectId,
    recipientId,
    senderId,
    senderName,
    content,
    senderType
  }) {
    try {
      console.log('🔔 [Brand Dashboard] Creating message notification:', {
        projectId,
        recipientId,
        senderId,
        senderName,
        senderType,
        contentLength: content?.length
      });
      
      const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId);
      if (!normalizedProjectId) {
        throw new Error('projectId is required for notifications');
      }
      const notificationsRef = collection(db, 'notifications');
      const recentNotificationsQuery = query(
        notificationsRef,
        where('userId', '==', recipientId),
        where('type', '==', 'chat'), // Changed from 'message' to 'chat' to match notification type
        where('data.projectId', '==', normalizedProjectId),
        orderBy('createdAt', 'desc')
      );

      const recentSnapshot = await getDocs(recentNotificationsQuery);
      const now = Date.now();
      const hasRecentNotification = recentSnapshot.docs.some((docSnap) => {
        const notif = docSnap.data();
        const notifTime = notif.createdAt?.toDate ? notif.createdAt.toDate().getTime() : new Date(notif.createdAt).getTime();
        return (now - notifTime) < 5000;
      });

      if (hasRecentNotification) {
        return;
      }

      const chatDoc = await getDoc(this.projectChatDoc(normalizedProjectId));
      const chatData = chatDoc.exists() ? chatDoc.data() : {};
      const campaignName = chatData.campaignName || 'Campaign';
      const briefName = chatData.briefName || 'Brief';
      const projectName = briefName || campaignName || 'Project';
      
      // Fetch the actual sender name from their user document
      // This ensures we always have the correct name for notifications
      let displayName = senderName;
      if (senderType === 'creator') {
        try {
          // Use senderId (the actual creator who sent the message) to fetch their name
          // Creators are stored in the 'users' collection, not 'creators' collection
          const creatorId = senderId || chatData.creatorId;
          if (creatorId) {
            const creatorDoc = await getDoc(doc(db, 'users', creatorId));
            if (creatorDoc.exists()) {
              const creatorData = creatorDoc.data();
              // Prefer displayName (set during onboarding), then name, then handle, then fallback to chat document or senderName
              // displayName is the name set during onboarding, so it should take priority
              displayName = creatorData.displayName 
                || creatorData.name 
                || creatorData.handle 
                || chatData.creatorName 
                || senderName;
              console.log('✅ Notification: Using creator name from Firestore:', displayName, 'from creatorId:', creatorId, 'displayName:', creatorData.displayName, 'name:', creatorData.name);
            } else {
              // Fallback to chat document creatorName if creator doc doesn't exist
              displayName = chatData.creatorName || senderName;
              console.log('⚠️ Notification: Creator doc not found in users collection, using chatData.creatorName:', displayName);
            }
          } else {
            displayName = chatData.creatorName || senderName;
            console.log('⚠️ Notification: No creatorId available, using chatData.creatorName:', displayName);
          }
        } catch (error) {
          console.error('❌ Error fetching creator name for notification:', error);
          // Fallback to chat document creatorName or senderName
          displayName = chatData.creatorName || senderName;
        }
      } else if (senderType === 'brand') {
        try {
          // Use senderId (the actual brand who sent the message) to fetch their name
          const brandId = senderId || chatData.brandUserId;
          console.log('🔍 Fetching brand name for notification - brandId:', brandId, 'senderId:', senderId, 'chatData.brandUserId:', chatData.brandUserId);
          
          if (brandId) {
            const brandDoc = await getDoc(doc(db, 'users', brandId));
            if (brandDoc.exists()) {
              const brandData = brandDoc.data();
              console.log('📄 Brand document data:', {
                displayName: brandData.displayName,
                brandName: brandData.brandName,
                companyName: brandData.companyName,
                agencyName: brandData.agencyName,
                email: brandData.email,
                userType: brandData.userType
              });
              
              // Verify this is actually a brand user
              if (brandData.userType !== 'brand' && brandData.userType !== 'client') {
                console.warn('⚠️ User is not a brand/client, userType:', brandData.userType);
              }
              
              // Prefer displayName, then brandName, then companyName, then agencyName, then email, then fallback to chat document or senderName
              displayName = brandData.displayName 
                || brandData.brandName 
                || brandData.companyName 
                || brandData.agencyName 
                || (brandData.email ? brandData.email.split('@')[0] : null) // Use email username as fallback
                || chatData.brandName 
                || senderName;
              console.log('✅ Notification: Using brand name from Firestore:', displayName, 'from brandId:', brandId, 'original senderName:', senderName);
            } else {
              // Fallback to chat document brandName if brand doc doesn't exist
              displayName = chatData.brandName || senderName;
              console.log('⚠️ Notification: Brand doc not found in users collection, using chatData.brandName:', displayName, 'or senderName:', senderName);
            }
          } else {
            displayName = chatData.brandName || senderName;
            console.log('⚠️ Notification: No brandId available, using chatData.brandName:', displayName);
          }
        } catch (error) {
          console.error('❌ Error fetching brand name for notification:', error);
          // Fallback to chat document brandName or senderName
          displayName = chatData.brandName || senderName;
        }
      }

      const notificationData = {
        userId: recipientId,
        type: 'chat', // Changed from 'message' to 'chat' for proper styling
        title: `New message from ${displayName}`,
        message: campaignName, // Show campaign name
        projectId: normalizedProjectId,
        data: {
          projectId: normalizedProjectId,
          projectName,
          campaignName,
          briefName,
          senderName: displayName,
          senderType
        },
        read: false,
        createdAt: serverTimestamp()
      };

      await addDoc(notificationsRef, notificationData);
    } catch (error) {
      console.error('Brand Dashboard - Error creating message notification:', error);
    }
  }

  static async setTypingIndicator(projectId, userType, isTyping) {
    try {
      const typingRef = this.projectTypingDoc(ProjectIdGenerator.normalizeProjectId(projectId));
      const field = userType === 'brand' ? 'brandTyping' : 'creatorTyping';
      await setDoc(typingRef, {
        [field]: isTyping
      }, { merge: true });
    } catch (error) {
      console.error('Error setting typing indicator:', error);
    }
  }

  static async markMessagesAsRead(projectId, userId, userType) {
    try {
      const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(projectId);
      if (!normalizedProjectId) {
        return;
      }

      const chatRef = this.projectChatDoc(normalizedProjectId);
      if (!chatRef) {
        return;
      }
      const chatDoc = await getDoc(chatRef);

      if (!chatDoc.exists()) {
        return;
      }

      const messagesRef = this.projectMessagesCollection(normalizedProjectId);
      const messagesSnapshot = await getDocs(messagesRef);
      const unreadMessages = messagesSnapshot.docs.filter((docSnap) => !docSnap.data().readBy?.includes(userId));

      const updatePromises = unreadMessages.map((messageDoc) =>
        updateDoc(doc(db, 'projectChats', normalizedProjectId, 'messages', messageDoc.id), {
          readBy: arrayUnion(userId)
        })
      );
      await Promise.all(updatePromises);

      await updateDoc(chatRef, {
        [`unreadCount.${userType}`]: 0
      });

      await setDoc(this.projectTypingDoc(normalizedProjectId), {
        [userType === 'brand' ? 'brandLastSeen' : 'creatorLastSeen']: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }

  static subscribeToMessages(projectId, callback) {
    const messagesRef = this.projectMessagesCollection(ProjectIdGenerator.normalizeProjectId(projectId));
    const q = query(messagesRef, orderBy('timestamp', 'asc'));

    return onSnapshot(q, (snapshot) => {
      const messages = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      callback(messages);
    }, (error) => {
      console.error('Error listening to messages:', error);
    });
  }

  static subscribeToTyping(projectId, callback) {
    const typingRef = this.projectTypingDoc(ProjectIdGenerator.normalizeProjectId(projectId));
    return onSnapshot(typingRef, (docSnap) => {
      if (docSnap.exists()) {
        callback(docSnap.data());
      }
    }, (error) => {
      console.error('Error listening to typing:', error);
    });
  }

  static subscribeToChat(projectId, callback) {
    const chatRef = this.projectChatDoc(ProjectIdGenerator.normalizeProjectId(projectId));
    return onSnapshot(chatRef, (docSnap) => {
      if (docSnap.exists()) {
        callback({
          id: docSnap.id,
          chatId: docSnap.id,
          ...docSnap.data()
        });
      }
    }, (error) => {
      console.error('Error listening to chat:', error);
    });
  }

  static subscribeToUserChats(userId, userType, callback) {
    if (!userId || !userType) {
      return () => {};
    }

    const chatsRef = collection(db, 'projectChats');
    const q = query(chatsRef, where('participants', 'array-contains', String(userId).trim()));

    return onSnapshot(q, (snapshot) => {
      const chats = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          chatId: docSnap.id,
          ...data
        };
      }).filter((chat) => chat.participants?.includes(String(userId).trim()));

      chats.sort((a, b) => {
        const aTime = a.lastMessageAt?.toDate ? a.lastMessageAt.toDate().getTime() : (a.lastMessageAt instanceof Timestamp ? a.lastMessageAt.toMillis() : new Date(a.lastMessageAt || 0).getTime());
        const bTime = b.lastMessageAt?.toDate ? b.lastMessageAt.toDate().getTime() : (b.lastMessageAt instanceof Timestamp ? b.lastMessageAt.toMillis() : new Date(b.lastMessageAt || 0).getTime());
        return bTime - aTime;
      });

      callback(chats);
    }, (error) => {
      console.error('Error listening to user chats:', error);
    });
  }

  static async closeChat(projectId) {
    try {
      await updateDoc(this.projectChatDoc(ProjectIdGenerator.normalizeProjectId(projectId)), {
        status: 'closed',
        closedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error closing chat:', error);
      throw error;
    }
  }

  static async reopenChat(projectId) {
    try {
      await updateDoc(this.projectChatDoc(ProjectIdGenerator.normalizeProjectId(projectId)), {
        status: 'active',
        closedAt: null
      });
    } catch (error) {
      console.error('Error reopening chat:', error);
      throw error;
    }
  }
}

export default ChatService;

